// F8 / FR-RD15: desktop Zoom/Teams meeting capture — UI-independent logic.
//
// Detection only ever *offers* a recording (never auto-start). The HUD (PR4) is the
// on-screen indicator and cannot be hidden while a meeting recording runs. Consent
// (F17 ConsentAnnouncement) gates the start when the org mode requires it. Wired up by
// useMeetingDetector (poll + notify), MeetingDetectedBanner, MeetingStartPanel, RecorderPage
// (?meeting=…), useMeetingAutoStop and the HUD label.
import { invoke, isDesktop } from "../desktop";

export type MeetingApp = "zoom" | "teams";
export type CapturePlatform = "zoom_desktop" | "teams_desktop";

export interface MeetingWindow {
  source_id: string;
  app: MeetingApp;
  capture_platform: CapturePlatform;
  title: string;
}

/** Poll interval for `meeting_windows` while the app is open (brief: every 5 s). */
export const MEETING_POLL_MS = 5000;
/** Auto-stop countdown after the meeting window closes (README assumption 8). */
export const WINDOW_CLOSE_COUNTDOWN_S = 10;

export async function listMeetingWindows(): Promise<MeetingWindow[]> {
  if (!isDesktop()) return [];
  return invoke<MeetingWindow[]>("meeting_windows");
}

export function appLabel(app: MeetingApp): string {
  return app === "zoom" ? "Zoom" : "Teams";
}

/** Banner copy: "Zoom meeting detected — Record this meeting?" */
export function detectedBannerText(app: MeetingApp): string {
  return `${appLabel(app)} meeting detected — Record this meeting?`;
}

/** Coral HUD label while recording: "REC · Zoom meeting". */
export function hudRecLabel(app: MeetingApp): string {
  return `REC · ${appLabel(app)} meeting`;
}

/** Windows we have not offered yet (one offer per window id per app session). */
export function newlyDetected(current: MeetingWindow[], offered: ReadonlySet<string>): MeetingWindow[] {
  return current.filter((w) => !offered.has(w.source_id));
}

const OFFER_KEY = "grackle.offerDesktopMeetingCapture";

/** Per-user "Offer to record detected Zoom/Teams meetings" (default on). */
export function getOfferSetting(storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage): boolean {
  try {
    return storage?.getItem(OFFER_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setOfferSetting(on: boolean, storage: Pick<Storage, "setItem"> | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(OFFER_KEY, on ? "on" : "off");
  } catch {
    /* storage unavailable: setting stays at its default */
  }
}

export interface StartGate {
  /** Org admin "Allow desktop meeting capture" (server enforces it too). */
  orgAllows: boolean;
  /** Org consent mode requires the announcement to be confirmed first (FR-M4 / F17). */
  consentRequired: boolean;
  consentConfirmed: boolean;
  /** System audio availability on this OS build. */
  systemAudioAvailable: boolean;
  /** User acknowledged the "other participants won't be heard" warning. */
  noSystemAudioAcknowledged: boolean;
}

export type StartBlock = "org_policy_off" | "consent_pending" | "system_audio_warning" | null;

/** Why recording cannot start yet, or null when it may. */
export function startBlockedBy(g: StartGate): StartBlock {
  if (!g.orgAllows) return "org_policy_off";
  if (g.consentRequired && !g.consentConfirmed) return "consent_pending";
  if (!g.systemAudioAvailable && !g.noSystemAudioAcknowledged) return "system_audio_warning";
  return null;
}

export const NO_SYSTEM_AUDIO_WARNING =
  "Other participants won't be heard: system audio isn't available on this OS build";

/**
 * Window-close auto-stop. Call `update(stillOpen)` from each poll; when the window is gone a
 * countdown starts (onTick with seconds left) and `onStop` fires after it unless the window
 * returns. `cancel()` clears it (e.g. the user stopped manually).
 */
export function createWindowCloseWatcher(opts: {
  seconds?: number;
  onTick: (secondsLeft: number | null) => void;
  onStop: () => void;
}) {
  const total = opts.seconds ?? WINDOW_CLOSE_COUNTDOWN_S;
  let timer: ReturnType<typeof setInterval> | null = null;
  let left = total;
  let stopped = false;
  const clear = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };
  return {
    update(stillOpen: boolean) {
      if (stopped) return;
      if (stillOpen) {
        if (timer) {
          clear();
          opts.onTick(null);
        }
        return;
      }
      if (timer) return;
      left = total;
      opts.onTick(left);
      timer = setInterval(() => {
        left -= 1;
        if (left <= 0) {
          clear();
          stopped = true;
          opts.onTick(0);
          opts.onStop();
        } else {
          opts.onTick(left);
        }
      }, 1000);
    },
    cancel() {
      clear();
      stopped = true;
    },
  };
}
